import {makeAutoObservable} from "mobx";
import {inject, injectable} from "inversify";
import {ViewController} from "data/types/structure";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";
import {ConferenceType, GameSteps} from "data/enums";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";

export interface IChipDivision {
	id: number | null;
	conferenceType: ConferenceType;
	step: GameSteps;
	name: string;
	isStepPassed: boolean;
	isStepActive: boolean;
}

export interface ILPDivisionSelectorController extends ViewController {
	readonly i18n: ILocalizationStore;

	get activeStepIndex(): number;
	get divisions(): IChipDivision[];
	get currentStep(): GameSteps;

	onSetStep: (step: GameSteps) => void;
}

@injectable()
export class LPDivisionSelectorController implements ILPDivisionSelectorController {
	get activeStepIndex() {
		return this._lpStore.currentStepIndex;
	}

	get divisions() {
		return this._lpStore.steps.map((step) => {
			const division = this._divisionStore.divisions.find(
				(division) => division.abbreviation === step
			);
			const conferenceType = this._lpStore.getConferenceByType(step);

			return {
				id: division?.id || null,
				conferenceType,
				step,
				name: this.getChipName(step),
				isStepPassed: this.getIsStepPassed(step, conferenceType),
				isStepActive: this.getIsStepActive(step),
			};
		});
	}

	get currentStep() {
		return this._lpStore.currentStep;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore
	) {
		makeAutoObservable(this);
	}

	private getIsStepActive(step: GameSteps) {
		const activeStep = this._lpStore.steps.find((step) => {
			const conferenceType = this._lpStore.getConferenceByType(step);
			return !this.getIsStepPassed(step, conferenceType);
		});

		return step === activeStep;
	}

	private getIsStepPassed(step: GameSteps, conferenceType: ConferenceType) {
		const predictions = this._lpStore.lightPrediction;
		const prediction = predictions[conferenceType].filter(
			(prediction) => prediction.type === step
		);

		if (step === GameSteps.NFC_SEEDING) {
			return this._lpStore.customSteps.nfc.seeding;
		}

		if (step === GameSteps.NFC_WILD_CARD) {
			return this._lpStore.customSteps.nfc.wildCard;
		}

		if (step === GameSteps.AFC_SEEDING) {
			return this._lpStore.customSteps.afc.seeding;
		}

		if (step === GameSteps.AFC_WILD_CARD) {
			return this._lpStore.customSteps.afc.wildCard;
		}

		return prediction.every(({squadId}) => squadId);
	}

	private getChipName(step: GameSteps) {
		return this.i18n.t(`navigation.division.${step}`);
	}

	onSetStep = (step: GameSteps) => {
		this._lpStore.setCurrentStep(step);
	};
}
